/**
 * Boat Markers Visualization
 * PERFORMANCE OPTIMIZED: Canvas-based rendering with minimal DOM updates
 */

import * as d3 from 'd3';

export class BoatRenderer {
  constructor(svgElement, mapController) {
    this.svg = d3.select(svgElement);
    this.map = mapController;

    // Create a dedicated canvas for boats (layered above trails)
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'boats-canvas';
    this.canvas.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 10;
    `;
    document.getElementById('map-container').appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    // Tooltip (keep as DOM for rich interactions)
    this.tooltip = d3.select('#team-tooltip');

    // State
    this.teams = [];
    this.selectedTeam = null;
    this.hoveredTeam = null;
    this.rafId = null;

    // Callbacks
    this.onTeamClick = null;
    this.onTeamHover = null;

    // Hit detection
    this.hitRadius = 20;

    this.init();
  }

  init() {
    this.resizeCanvas();

    // Resize handler
    const resizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
      this.draw();
    });
    resizeObserver.observe(this.canvas.parentElement);

    // Mouse interaction on the map container
    const container = document.getElementById('map-container');

    container.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    container.addEventListener('click', (e) => this.handleClick(e));
    container.addEventListener('mouseleave', () => this.handleMouseLeave());
  }

  resizeCanvas() {
    const rect = this.canvas.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;

    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Render boat markers
   */
  render(teams, selectedTeam = null) {
    this.teams = teams.filter(t => t.interpolated || t.latest);
    this.selectedTeam = selectedTeam;
    this.draw();
  }

  /**
   * High-performance canvas drawing
   * @param {boolean} immediate - Skip RAF scheduling for sync with map
   */
  draw(immediate = false) {
    // Always draw immediately for map sync - canvas draw is fast
    this._drawInternal();
  }

  _drawInternal() {
    const ctx = this.ctx;
    const width = this.canvas.width / (window.devicePixelRatio || 1);
    const height = this.canvas.height / (window.devicePixelRatio || 1);

    ctx.clearRect(0, 0, width, height);

    if (!this.teams || this.teams.length === 0) return;

    // Sort: selected on top, then hovered, then by rank
    const sortedTeams = [...this.teams].sort((a, b) => {
      if (a.serial === this.selectedTeam) return 1;
      if (b.serial === this.selectedTeam) return -1;
      if (a.serial === this.hoveredTeam) return 1;
      if (b.serial === this.hoveredTeam) return -1;
      return 0;
    });

    sortedTeams.forEach((team, index) => {
      const pos = team.interpolated || team.latest;
      if (!pos || isNaN(pos.lng) || isNaN(pos.lat)) return;

      const [x, y] = this.map.project(pos.lng, pos.lat);
      const isSelected = team.serial === this.selectedTeam;
      const isHovered = team.serial === this.hoveredTeam;
      const rank = this.teams.findIndex(t => t.serial === team.serial) + 1;

      // Draw pulse ring
      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(x, y, 18, 0, Math.PI * 2);
        ctx.strokeStyle = team.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.3;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Draw glow
      ctx.beginPath();
      ctx.arc(x, y, isSelected ? 14 : 10, 0, Math.PI * 2);
      ctx.fillStyle = team.color;
      ctx.globalAlpha = isSelected ? 0.4 : 0.2;
      ctx.shadowColor = team.color;
      ctx.shadowBlur = isSelected ? 15 : 8;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;

      // Draw boat icon (diamond/arrow shape pointing in direction of travel)
      const size = isSelected || isHovered ? 10 : 7;
      const cog = (pos.cog || 0) * Math.PI / 180;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(cog);

      // Boat shape
      ctx.beginPath();
      ctx.moveTo(0, -size * 1.3);  // Bow
      ctx.lineTo(size * 0.6, size * 0.3);
      ctx.lineTo(0, size * 0.8);
      ctx.lineTo(-size * 0.6, size * 0.3);
      ctx.closePath();

      ctx.fillStyle = team.color;
      ctx.shadowColor = team.color;
      ctx.shadowBlur = 6;
      ctx.fill();
      ctx.shadowBlur = 0;

      // Boat outline
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.restore();

      // Draw rank badge for top 3
      if (rank <= 3) {
        ctx.font = 'bold 10px JetBrains Mono, monospace';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#f5a623';
        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 4;
        ctx.fillText(`#${rank}`, x, y - 18);
        ctx.shadowBlur = 0;
      }
    });
  }

  /**
   * Handle mouse move for hover detection
   */
  handleMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const hoveredTeam = this.findTeamAtPoint(x, y);

    if (hoveredTeam !== this.hoveredTeam) {
      this.hoveredTeam = hoveredTeam;
      this.draw();

      if (hoveredTeam) {
        const team = this.teams.find(t => t.serial === hoveredTeam);
        if (team) {
          this.showTooltip(e, team);
        }
      } else {
        this.hideTooltip();
      }

      // Update cursor
      this.canvas.parentElement.style.cursor = hoveredTeam ? 'pointer' : '';

      if (this.onTeamHover) {
        const team = hoveredTeam ? this.teams.find(t => t.serial === hoveredTeam) : null;
        this.onTeamHover(team);
      }
    } else if (hoveredTeam) {
      // Update tooltip position
      this.updateTooltipPosition(e);
    }
  }

  /**
   * Handle click for team selection
   */
  handleClick(e) {
    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const clickedTeam = this.findTeamAtPoint(x, y);
    if (clickedTeam && this.onTeamClick) {
      const team = this.teams.find(t => t.serial === clickedTeam);
      if (team) {
        this.onTeamClick(team);
      }
    }
  }

  /**
   * Handle mouse leave
   */
  handleMouseLeave() {
    if (this.hoveredTeam) {
      this.hoveredTeam = null;
      this.draw();
      this.hideTooltip();
    }
  }

  /**
   * Find team at given screen coordinates
   */
  findTeamAtPoint(x, y) {
    for (let i = this.teams.length - 1; i >= 0; i--) {
      const team = this.teams[i];
      const pos = team.interpolated || team.latest;
      if (!pos || isNaN(pos.lng) || isNaN(pos.lat)) continue;

      const [tx, ty] = this.map.project(pos.lng, pos.lat);
      const dist = Math.hypot(tx - x, ty - y);

      if (dist <= this.hitRadius) {
        return team.serial;
      }
    }
    return null;
  }

  /**
   * Show tooltip
   */
  showTooltip(event, team) {
    const pos = team.interpolated || team.latest;
    const rank = this.teams.findIndex(t => t.serial === team.serial) + 1;

    this.tooltip.select('.tooltip-rank').text(`#${rank}`);
    this.tooltip.select('.tooltip-name').text(team.name);
    this.tooltip.select('.stat-value.speed').text(`${pos.speed.toFixed(1)} kn`);
    this.tooltip.select('.stat-value.dtf').text(`${pos.dtf.toFixed(0)} nm`);
    this.tooltip.select('.stat-value.cog').text(`${Math.round(pos.cog)}°`);
    this.tooltip.select('.stat-value.battery')
      .text(pos.battery !== undefined ? `${pos.battery}%` : '--');

    this.tooltip.classed('hidden', false);
    this.updateTooltipPosition(event);
  }

  /**
   * Update tooltip position
   */
  updateTooltipPosition(event) {
    const tooltipNode = this.tooltip.node();
    const rect = tooltipNode.getBoundingClientRect();

    let x = event.clientX + 15;
    let y = event.clientY - rect.height / 2;

    if (x + rect.width > window.innerWidth - 20) {
      x = event.clientX - rect.width - 15;
    }
    if (y < 20) y = 20;
    if (y + rect.height > window.innerHeight - 20) {
      y = window.innerHeight - rect.height - 20;
    }

    this.tooltip
      .style('left', `${x}px`)
      .style('top', `${y}px`);
  }

  /**
   * Hide tooltip
   */
  hideTooltip() {
    this.tooltip.classed('hidden', true);
  }

  /**
   * Update positions for timeline
   */
  updatePositions(teamsAtTime) {
    this.teams = teamsAtTime.filter(t => t.interpolated);
    this.draw();
  }

  /**
   * Set click handler
   */
  setClickHandler(handler) {
    this.onTeamClick = handler;
  }

  /**
   * Set hover handler
   */
  setHoverHandler(handler) {
    this.onTeamHover = handler;
  }

  /**
   * Clear
   */
  clear() {
    this.teams = [];
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.hideTooltip();
  }
}
